/** Ephemeral evaluation requests: Query owns cancellation and fetching, including the typing delay. */
import { useCallback, useId, useMemo } from 'react'
import { useIsFetching, useQueryClient } from '@tanstack/react-query'

function debounce(ms: number, signal: AbortSignal): Promise<void> {
  signal.throwIfAborted()
  if (ms === 0) return Promise.resolve()
  return new Promise((resolve, reject) => {
    const aborted = () => {
      clearTimeout(timer)
      reject(signal.reason)
    }
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', aborted)
      resolve()
    }, ms)
    signal.addEventListener('abort', aborted, { once: true })
  })
}

export function useBuilderEvaluationQuery(visit: string, draftId: string) {
  const client = useQueryClient()
  const mount = useId()
  const scope = useMemo(() => ['shopify-builder-evaluation', visit, draftId, mount], [visit, draftId, mount])
  const checking = useIsFetching({ queryKey: scope }) > 0
  const cancel = useCallback(() => {
    // Reverting a cancelled query can return its old data to fetchQuery. Cancelled work must reject instead.
    void client.cancelQueries({ queryKey: scope }, { revert: false })
  }, [client, scope])
  const run = useCallback(
    <T>(work: (signal: AbortSignal) => Promise<T>, delay = 0): Promise<T> => {
      cancel()
      return client.fetchQuery({
        // A retry/Save is a fresh request even when its input matches: never reuse a settled answer or old queryFn.
        queryKey: [...scope, crypto.randomUUID()],
        queryFn: async ({ signal }) => {
          await debounce(delay, signal)
          signal.throwIfAborted()
          return work(signal)
        },
        retry: false,
        networkMode: 'always',
        staleTime: 0,
        gcTime: 0,
      })
    },
    [cancel, client, scope],
  )
  return { checking, cancel, run }
}
